<template>
  <div ref="containerRef" :class="ns.e('univer')" />
</template>

<script setup lang="ts">
import { useGlobalConfig } from '@zoryn/components/config-provider'
import { useLocale, useNamespace } from '@zoryn/hooks'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue'
import {
  buildDataRangeNotation,
  buildRowErrorMessages,
  buildSheetRangeNotation,
  columnIndexToLetters,
  getSheetValidationColors,
  resolveDocumentDark,
  resolveElementDark,
  resolveScanRowCount,
  rowsToSheetMatrix,
  sheetMatrixToRows,
  resolveErrorHighlightRange,
  UNIVER_DEFAULT_SHEET_ROW_COUNT,
  UNIVER_HEADER_ROW_COUNT,
} from './univer-bridge'
import type { GridImportColumn, ImportRowError } from '../types'

const props = withDefaults(
  defineProps<{
    columns: GridImportColumn[]
    rows: Record<string, unknown>[]
    maxRows?: number
    validationErrors?: ImportRowError[]
  }>(),
  {
    maxRows: 5000,
    validationErrors: () => [],
  }
)

const ns = useNamespace('grid-import')
const { lang, t } = useLocale()
const configDark = useGlobalConfig('dark')
const containerRef = ref<HTMLElement | null>(null)
const ready = ref(false)
/** 文档主题变化时递增，驱动 isDark 重新计算 */
const documentThemeTick = ref(0)

const isDark = computed(() => {
  // 读取 documentThemeTick 建立依赖，主题 DOM 变化时强制重算
  if (documentThemeTick.value >= 0 && resolveDocumentDark()) return true
  if (configDark.value === true) return true
  if (resolveElementDark(containerRef.value)) return true
  if (configDark.value === false) return false
  return false
})

type UniverStyleData = {
  bg?: { rgb: string }
  cl?: { rgb: string }
}

type UniverRuntime = {
  univer: { dispose: () => void }
  univerAPI: {
    dispose: () => void
    getActiveWorkbook: () => UniverWorkbook | null
    toggleDarkMode?: (enabled: boolean) => void
    createWorkbook: (data?: Record<string, unknown>) => unknown
  }
}

type UniverWorkbook = {
  getActiveSheet: () => UniverWorksheet | null
}

type UniverWorksheet = {
  getRange: (range: string) => UniverRange
  setColumnWidth: (column: number, width: number) => UniverWorksheet
  setFrozenRows: (count: number) => UniverWorksheet
  setDefaultStyle?: (style: UniverStyleData | null | undefined) => void
  setColumnDefaultStyle?: (
    index: number,
    style: UniverStyleData | null | undefined
  ) => void
}

type UniverRange = {
  setValues: (values: unknown[][]) => UniverRange
  getValues: () => unknown[][]
  setBackground: (color: string) => UniverRange
  setBackgroundColor?: (color: string) => UniverRange
  setFontColor: (color: string | null) => UniverRange
  getFontColor?: () => string
  getFontColors?: () => string[][]
  setValue: (value: unknown) => UniverRange
  clearFormat?: () => UniverRange
  getCellStyle?: () => unknown
  getCellDatas?: () => unknown
}

/**
 * 常规单元格不写底色：显式白底会盖住 Univer 网格线。
 * 暗色还不写字色（darkMode 会 invert，白字变近黑）。
 * 浅色只强制深色字。
 */
function applyRangeTheme(
  range: UniverRange,
  _background: string,
  fontColor: string,
  dark = false
) {
  range.clearFormat?.()
  if (dark) return
  range.setFontColor(fontColor)
}

/** 校验错误高亮：需要显式底色（会盖住该格网格线，属预期） */
function applyErrorFill(
  range: UniverRange,
  background: string,
  fontColor: string
) {
  if (typeof range.setBackgroundColor === 'function') {
    range.setBackgroundColor(background)
  } else {
    range.setBackground(background)
  }
  range.setFontColor(fontColor)
}

const runtime = shallowRef<UniverRuntime | null>(null)
const sheetDarkMode = ref(false)
let documentThemeObserver: MutationObserver | null = null

function getWorksheet() {
  return runtime.value?.univerAPI.getActiveWorkbook()?.getActiveSheet() ?? null
}

function resolveSheetDark() {
  const dark = isDark.value
  sheetDarkMode.value = dark
  return dark
}

function applyWorksheetDefaultStyle(dark: boolean) {
  const worksheet = getWorksheet()
  if (!worksheet) return
  // 不设默认底色，避免盖住网格线；暗色也不设字色（交给 invert）
  worksheet.setDefaultStyle?.(null)
  for (let index = 0; index < props.columns.length; index += 1) {
    worksheet.setColumnDefaultStyle?.(index, null)
  }
  if (dark) return
  const { cellFontColor } = getSheetValidationColors(false)
  const fontOnly = { cl: { rgb: cellFontColor } }
  worksheet.setDefaultStyle?.(fontOnly)
  for (let index = 0; index < props.columns.length; index += 1) {
    worksheet.setColumnDefaultStyle?.(index, fontOnly)
  }
}

function writeSheetData() {
  const worksheet = getWorksheet()
  if (!worksheet || !props.columns.length) return

  const dark = resolveSheetDark()
  sheetDarkMode.value = dark
  const { cellBackground, cellFontColor } = getSheetValidationColors(dark)
  applyWorksheetDefaultStyle(dark)

  // 始终纯值：不写 bg，避免盖住网格线；暗色也不写 cl（避免 invert）
  const matrix = rowsToSheetMatrix(props.columns, props.rows)
  const rowCount = Math.min(
    Math.max(matrix.length, props.rows.length + 1, 2),
    UNIVER_DEFAULT_SHEET_ROW_COUNT
  )
  const colCount = props.columns.length
  const range = worksheet.getRange(buildSheetRangeNotation(rowCount, colCount))
  // #region agent log
  const sample = matrix[0]?.[0] as
    | { v?: unknown; s?: { cl?: { rgb?: string }; bg?: { rgb?: string } } }
    | string
    | undefined
  fetch('http://127.0.0.1:7826/ingest/c4c6e4fe-09d8-4594-a5a0-28b897d88389', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Debug-Session-Id': 'e1579f',
    },
    body: JSON.stringify({
      sessionId: 'e1579f',
      runId: 'gridline-fix',
      hypothesisId: 'E',
      location: 'import-univer-preview.vue:writeSheetData:beforeSetValues',
      message: 'plain matrix both themes; no cell bg to keep gridlines',
      data: {
        dark,
        strategy: 'plain-no-bg',
        cellFontColor,
        sample,
        sampleIsPlain: typeof sample === 'string' || typeof sample === 'number',
      },
      timestamp: Date.now(),
    }),
  }).catch(() => {})
  // #endregion
  range.setValues(matrix)
  applyRangeTheme(range, cellBackground, cellFontColor, dark)
  // #region agent log
  let fontAfter: string | null = null
  let styleAfter: unknown = null
  let a1Font: string | null = null
  let hasBg = false
  try {
    fontAfter =
      typeof range.getFontColor === 'function' ? range.getFontColor() : null
    styleAfter =
      typeof range.getCellStyle === 'function' ? range.getCellStyle() : null
    const styleObj = styleAfter as { _style?: { bg?: unknown; cl?: unknown } }
    hasBg = Boolean(styleObj?._style?.bg)
    const a1 = worksheet.getRange('A1')
    a1Font = typeof a1.getFontColor === 'function' ? a1.getFontColor() : null
  } catch (error) {
    fontAfter = `error:${(error as Error)?.message ?? 'unknown'}`
  }
  fetch('http://127.0.0.1:7826/ingest/c4c6e4fe-09d8-4594-a5a0-28b897d88389', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Debug-Session-Id': 'e1579f',
    },
    body: JSON.stringify({
      sessionId: 'e1579f',
      runId: 'gridline-fix',
      hypothesisId: 'E',
      location: 'import-univer-preview.vue:writeSheetData:afterApplyRangeTheme',
      message: 'style readback; hasBg should be false for gridlines',
      data: {
        dark,
        intendedFg: cellFontColor,
        fontAfter,
        a1Font,
        styleAfter,
        hasBg,
        expectNoBg: true,
      },
      timestamp: Date.now(),
    }),
  }).catch(() => {})
  // #endregion

  for (let index = 0; index < colCount; index += 1) {
    worksheet.setColumnWidth(index, 150)
  }
  worksheet.setFrozenRows(1)
  applyValidationErrors(props.validationErrors)

  // #region agent log
  let fontFinal: string | null = null
  let styleFinal: unknown = null
  let hasBgFinal = false
  try {
    const a1 = worksheet.getRange('A1')
    fontFinal = typeof a1.getFontColor === 'function' ? a1.getFontColor() : null
    styleFinal =
      typeof a1.getCellStyle === 'function' ? a1.getCellStyle() : null
    const styleObj = styleFinal as { _style?: { bg?: unknown } }
    hasBgFinal = Boolean(styleObj?._style?.bg)
  } catch (error) {
    fontFinal = `error:${(error as Error)?.message ?? 'unknown'}`
  }
  fetch('http://127.0.0.1:7826/ingest/c4c6e4fe-09d8-4594-a5a0-28b897d88389', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Debug-Session-Id': 'e1579f',
    },
    body: JSON.stringify({
      sessionId: 'e1579f',
      runId: 'gridline-fix',
      hypothesisId: 'E',
      location: 'import-univer-preview.vue:writeSheetData:afterValidation',
      message: 'A1 after validation; no bg keeps gridlines',
      data: {
        fontFinal,
        styleFinal,
        intendedFg: cellFontColor,
        dark,
        hasBgFinal,
      },
      timestamp: Date.now(),
    }),
  }).catch(() => {})
  // #endregion
}

function readRows() {
  const worksheet = getWorksheet()
  if (!worksheet || !props.columns.length) return []

  const scanRowCount = resolveScanRowCount(props.rows.length)
  const range = worksheet.getRange(
    buildDataRangeNotation(scanRowCount, props.columns.length)
  )
  const matrix = range.getValues()
  return sheetMatrixToRows(
    props.columns,
    [props.columns.map(() => ''), ...matrix],
    {
      maxRows: props.maxRows,
    }
  )
}

function syncErrorColumn(
  worksheet: UniverWorksheet,
  errors: ImportRowError[],
  errorBackground: string,
  errorFontColor: string,
  cellBackground: string,
  cellFontColor: string,
  dark: boolean
) {
  const errorColIndex = props.columns.length
  const errorCol = columnIndexToLetters(errorColIndex)
  const sheetRowCount = Math.min(
    Math.max(props.rows.length + UNIVER_HEADER_ROW_COUNT, 2),
    UNIVER_DEFAULT_SHEET_ROW_COUNT
  )
  const clearRange = worksheet.getRange(
    `${errorCol}1:${errorCol}${sheetRowCount}`
  )

  if (!errors.length) {
    clearRange.setValues(Array.from({ length: sheetRowCount }, () => ['']))
    applyRangeTheme(clearRange, cellBackground, cellFontColor, dark)
    worksheet.setColumnWidth(errorColIndex, 80)
    return
  }

  const headerCell = worksheet.getRange(`${errorCol}1`)
  headerCell.setValue(t('z.import.validateCol'))
  applyRangeTheme(headerCell, cellBackground, cellFontColor, dark)
  worksheet.setColumnWidth(errorColIndex, 280)

  const messages = buildRowErrorMessages(errors, props.rows.length)
  messages.forEach((message, index) => {
    if (!message) return
    const rowNumber = index + UNIVER_HEADER_ROW_COUNT + 1
    const cell = worksheet.getRange(`${errorCol}${rowNumber}`)
    cell.setValue(message)
    applyErrorFill(cell, errorBackground, errorFontColor)
  })
}

function applyValidationErrors(errors: ImportRowError[]) {
  const worksheet = getWorksheet()
  if (!worksheet || !props.columns.length) return

  const dark = resolveSheetDark()
  const { cellBackground, cellFontColor, errorBackground, errorFontColor } =
    getSheetValidationColors(dark)
  const paintedRowCount = Math.min(
    Math.max(props.rows.length + UNIVER_HEADER_ROW_COUNT, 2),
    UNIVER_DEFAULT_SHEET_ROW_COUNT
  )
  const dataRange = worksheet.getRange(
    buildSheetRangeNotation(paintedRowCount, props.columns.length)
  )

  applyRangeTheme(dataRange, cellBackground, cellFontColor, dark)

  errors.forEach((error) => {
    const range = resolveErrorHighlightRange(error, props.columns)
    if (!range) return
    applyErrorFill(worksheet.getRange(range), errorBackground, errorFontColor)
  })

  syncErrorColumn(
    worksheet,
    errors,
    errorBackground,
    errorFontColor,
    cellBackground,
    cellFontColor,
    dark
  )
}

function disposeUniver() {
  runtime.value?.univer.dispose()
  runtime.value?.univerAPI.dispose()
  runtime.value = null
  ready.value = false
}

function syncUniverDarkMode(enabled: boolean) {
  const api = runtime.value?.univerAPI
  if (!api) return false
  sheetDarkMode.value = enabled
  if (typeof api.toggleDarkMode === 'function') {
    api.toggleDarkMode(enabled)
    return true
  }
  return false
}

function buildInitialWorkbookData(dark: boolean) {
  const { cellFontColor } = getSheetValidationColors(dark)
  const sheet: Record<string, unknown> = {
    id: 'sheet-1',
    name: 'Sheet1',
  }
  // 不设默认底色（否则盖住网格线）；暗色不设字色（避免 invert）
  if (!dark) {
    sheet.defaultStyle = { cl: { rgb: cellFontColor } }
  }
  return {
    id: 'z-grid-import',
    sheetOrder: ['sheet-1'],
    sheets: {
      'sheet-1': sheet,
    },
  }
}

async function mountUniver() {
  if (!containerRef.value) return

  const [
    { createUniver, LocaleType, mergeLocales },
    { UniverSheetsCorePreset },
    zhCNModule,
    enUSModule,
    { UniverVue3AdapterPlugin },
  ] = await Promise.all([
    import('@univerjs/presets'),
    import('@univerjs/preset-sheets-core'),
    import('@univerjs/preset-sheets-core/locales/zh-CN'),
    import('@univerjs/preset-sheets-core/locales/en-US'),
    import('@univerjs/ui-adapter-vue3'),
  ])

  const isZh = lang.value.toLowerCase().startsWith('zh')
  const localeType = isZh ? LocaleType.ZH_CN : LocaleType.EN_US
  const localePack = isZh ? zhCNModule.default : enUSModule.default
  const dark = isDark.value
  sheetDarkMode.value = dark

  const { univer, univerAPI } = createUniver({
    locale: localeType,
    darkMode: dark,
    locales: {
      [localeType]: mergeLocales(localePack),
    },
    presets: [
      UniverSheetsCorePreset({
        container: containerRef.value,
      }),
    ],
    plugins: [UniverVue3AdapterPlugin],
  })

  univerAPI.createWorkbook(buildInitialWorkbookData(dark))
  runtime.value = { univer, univerAPI }
  ready.value = true
  await nextTick()
  writeSheetData()
}

async function remountUniver() {
  disposeUniver()
  await nextTick()
  await mountUniver()
}

onMounted(() => {
  if (
    typeof MutationObserver !== 'undefined' &&
    typeof document !== 'undefined'
  ) {
    documentThemeObserver = new MutationObserver(() => {
      documentThemeTick.value += 1
    })
    documentThemeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-z-ui-theme'],
    })
  }
  void mountUniver()
})

onBeforeUnmount(() => {
  documentThemeObserver?.disconnect()
  documentThemeObserver = null
  disposeUniver()
})

watch(isDark, async (enabled, previous) => {
  if (previous === undefined || !ready.value) return
  if (syncUniverDarkMode(enabled)) {
    writeSheetData()
    return
  }
  await remountUniver()
})

watch(
  () => [props.rows, props.columns] as const,
  async () => {
    if (!ready.value) return
    await nextTick()
    writeSheetData()
  },
  { deep: true }
)

watch(
  () => props.validationErrors,
  (errors) => {
    if (!ready.value) return
    applyValidationErrors(errors)
  },
  { deep: true }
)

defineExpose({
  readRows,
  writeSheetData,
  applyValidationErrors,
})
</script>
